import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";

import { auth, db } from "../firebase";
import { UserData } from "../types/UserData";

interface CurrentUserContextType {
  currentUser: UserData | null;
  isCurrentUserLoading: boolean;
}

const CurrentUserContext = createContext<CurrentUserContextType | undefined>(
  undefined,
);

// ログイン中の自分自身のユーザー情報（role・judgeなど、権限の判定に使う）を
// アプリ全体で1つだけ持っておくためのProvider。
//
// 以前はSideMenuなど各画面ごとにFirestoreへ問い合わせていたため、
// 画面遷移でコンポーネントが再マウントされるたびに「未取得(null)→取得済み」
// という一瞬の空白が発生し、権限で出し分けている項目がそのたびに
// ちらついていた。
//
// ここではFirebase Authのログイン状態（onAuthStateChanged）が変化した
// タイミング＝ログイン・ログアウトの時だけ取得し直す。App全体をこの
// Providerで一度だけ包むので、以降は何ページ移動しても再取得は起きず、
// 各画面はコンテキストから同じ値を読むだけになる。
export const CurrentUserProvider = ({ children }: { children: ReactNode }) => {
  const [currentUser, setCurrentUser] = useState<UserData | null>(null);
  const [isCurrentUserLoading, setIsCurrentUserLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (authUser) => {
      if (!authUser) {
        setCurrentUser(null);
        setIsCurrentUserLoading(false);
        return;
      }

      try {
        const snap = await getDoc(doc(db, "users", authUser.uid));

        setCurrentUser(
          snap.exists()
            ? { id: snap.id, ...(snap.data() as Omit<UserData, "id">) }
            : null,
        );
      } catch (error) {
        console.error("currentUser取得エラー:", error);
        setCurrentUser(null);
      } finally {
        setIsCurrentUserLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  return (
    <CurrentUserContext.Provider
      value={{ currentUser, isCurrentUserLoading }}
    >
      {children}
    </CurrentUserContext.Provider>
  );
};

export const useCurrentUser = () => {
  const context = useContext(CurrentUserContext);

  if (!context) {
    throw new Error(
      "useCurrentUser must be used within CurrentUserProvider",
    );
  }

  return context;
};
